import styled, { x } from '@xstyled/styled-components';
import React, { useCallback, useEffect, useRef } from 'react';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { Button } from '~/ui/buttons/Button';
import { Flex } from '~/ui/flex/Flex';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { TrainingState, useTrainingStore } from '../state';
import { OnboardingStages, useOnboardingStore } from './Store';
import { useNextSequence } from '~/training/useNextSequence';
import { LargeLocaleSelector } from '~/ui/locale-selector/large-locale-selector/LargeLocaleSelector';
import { Root } from '~/training/settings/Root';
import { MediumLocalePage } from '~/training/settings/locales/MediumLocalePage';
import { TextLocalePage } from '~/training/settings/locales/TextLocalePage';
import { SubtitleLocalePage } from '~/training/settings/locales/SubtitleLocalePage';
import { DarkModePage } from '~/training/settings/locales/DarkModePage';
import { PlaybackSpeed } from '~/training/settings/playback-speed/PlaybackSpeed';
import { Overlay } from '~/ui/overlay/Overlay';
import { useSettingsStore, SettingsPages } from '~/training/settings/Store';
import { withTransition } from '~/ui/transition/Transition';
import { Card } from '~/ui/card/Card';
import { Logo } from '~/ui/logo/Logo';
import { getOnboardingTextLocales } from '~/training/settings/locales/getLanguagePickerLocales';
import { useArrowFocusList } from '~/lib/a11y/useArrowFocusList';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';

const LanguageSelectionWrapper = styled.div`
  .modalStyleForDarkTheme {
    height: fit-content;
  }
`;

const TrainingTitle = styled.div`
  font-size: 14px;
  text-align: center;
  font-weight: 700;
  color: black;
`;

const ActionsBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const SettingsButton = styled.button`
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  color: accentDark;
  cursor: pointer;
  margin-top: 12px;
  border: none;
  background: none;
  padding: 2px 4px;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-dk);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const SectionLogo = styled(Logo)`
  height: 40px;
  width: auto;
  margin: 0;
`;

const TitleBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-direction: column;
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'textLocales',
    'languages',
    'onboarding',
    'shouldDisplayCourseDurationCard',
    'disclaimer',
    'logoUrl',
    'title',
    'scorePassingPercent'
  );

const SettingsCard = withTransition(Card);

export const LanguageSelection = ({
  isOnboarding
}: {
  isOnboarding?: boolean;
}) => {
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const settingsDialogRef = useRef<HTMLDivElement>(null);
  const { stage, next, goTo } = useOnboardingStore();
  const {
    textLocales,
    onboarding,
    shouldDisplayCourseDurationCard,
    disclaimer,
    logoUrl,
    title,
    scorePassingPercent
  } = useTrainingStore(selector);
  const { open, toggle, page, setPage } = useSettingsStore();
  const nextSequence = useNextSequence();

  const openSettings = useCallback(() => {
    setPage(SettingsPages.Root);
    toggle(true);
  }, [setPage, toggle]);

  const closeSettings = useCallback(() => {
    toggle(false);
  }, [toggle]);

  useFocusTrap(settingsDialogRef, open, undefined, settingsButtonRef);
  useArrowFocusList(settingsDialogRef, open);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // Sub-pages: Escape acts like Back. Root: close settings.
      if (page !== SettingsPages.Root) {
        setPage(SettingsPages.Root);
        return;
      }

      closeSettings();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, page, setPage, closeSettings]);

  const GoNext = () => {
    if (onboarding) {
      return next();
    } else {
      if (shouldDisplayCourseDurationCard) {
        return goTo(OnboardingStages.CourseDuration);
      } else if (disclaimer) {
        return goTo(OnboardingStages.Disclaimer);
      } else {
        if (scorePassingPercent && scorePassingPercent > 0) {
          return goTo(OnboardingStages.PassFail);
        } else {
          nextSequence().then();
        }
      }
    }
  };

  const locales = getOnboardingTextLocales(textLocales);

  return (
    <LanguageSelectionWrapper>
      {stage === OnboardingStages.LanguageSelection && (
        <LlamaModal horizontal="center" vertical="center" allowScroll>
          <Flex
            data-test-id="language-selection-modal"
            minHeight="md"
            w="100%"
            minWidth={{ _: '100%', sm: 'lg' }}
            flexDirection="column"
            p="xl"
            alignItems="center"
            justifyContent="space-between"
            gap="22px"
          >
            <SectionLogo src={logoUrl} />

            <TitleBlock>
              <x.h2 fontFamily="Recoleta">{t`Welcome!`}</x.h2>

              <TrainingTitle>{title}</TrainingTitle>

              <small>{t`Please select your language:`}</small>
            </TitleBlock>

            <LargeLocaleSelector
              locales={locales}
              reloadSubtitle={true}
              isOnboarding={isOnboarding}
            />

            <ActionsBlock>
              <Button
                onClick={GoNext}
                disabled={stage !== OnboardingStages.LanguageSelection}
                aria-hidden={stage !== OnboardingStages.LanguageSelection}
                padding="11px 56px"
                data-test-id="continue-button"
              >
                {t`Continue`}
              </Button>
              <SettingsButton
                ref={settingsButtonRef}
                type="button"
                onClick={openSettings}
              >
                {t`Accessibility Settings`}
              </SettingsButton>
            </ActionsBlock>
          </Flex>
        </LlamaModal>
      )}

      <Overlay open={open} opacity={0.1} allowScroll onClick={closeSettings}>
        <Flex
          zIndex="dialogs"
          p={{ _: 'sm', sm: 'xl' }}
          minHeight="100%"
          justifyContent={{ _: 'center', sm: 'center' }}
          alignItems={{ _: 'center', sm: 'center' }}
          overflow="hidden"
        >
          <SettingsCard
            show={open}
            w={{ _: '100%', sm: 'auto' }}
            className="modalStyleForDarkTheme"
            onClick={e => e.stopPropagation()}
          >
            <div
              ref={settingsDialogRef}
              role="dialog"
              aria-modal="true"
              aria-label={t`Accessibility Settings`}
            >
              <Root headerTitle={t`Accessibility Settings`} />
              <MediumLocalePage />
              <TextLocalePage />
              <SubtitleLocalePage />
              <DarkModePage />
              <PlaybackSpeed />
            </div>
          </SettingsCard>
        </Flex>
      </Overlay>
    </LanguageSelectionWrapper>
  );
};
